import React from "react";
import { Box } from "@UI/Components/Box/Box";
import { Text } from "@UI/Components/Text/Text";
import { Rating } from "@UI/Components/Rating/Rating";
import {
  RecommendationCard,
  RecommendationCardProps,
} from "./RecommendationCard";
import { IRecommendedCategory } from "../../types/recommendations.types";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "./messages";

export type CategoryRecommendationCardProps = IRecommendedCategory & {
  /** Override the cover image height. Defaults to "140px". */
  coverHeight?: RecommendationCardProps["coverHeight"];
};

/**
 * CategoryRecommendationCard
 *
 * A RecommendationCard pre-wired for a recommended Category.
 * Accepts the same shape as IRecommendedCategory from the API.
 *
 * Layout:
 *   [category cover image]
 *   header: category name
 *   body:   description
 *   footer: star rating + ratings count
 */
const IMAGE_CDN = "https://imagedelivery.net/ZBo47-YbW4ydKQIMRxbMWw";

export const CategoryRecommendationCard = ({
  name,
  average_rating,
  ratings_count,
  image_slug,
  cta_path,
  coverHeight = "120px",
}: CategoryRecommendationCardProps) => {
  const trackedHref = cta_path
    ? `${cta_path}${
        cta_path.includes("?") ? "&" : "?"
      }from=home_listposter_category_recommendation`
    : undefined;

  return (
    <RecommendationCard
      coverImage={`${IMAGE_CDN}/sem-client-${image_slug}-og-image/public`}
      coverAlt={name}
      coverHeight={coverHeight}
      href={trackedHref}
      header={
        <Box
          fontWeight="bold"
          fontSize="md"
          color="gray.800"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {name}
        </Box>
      }
      footer={
        average_rating != null ? (
          <Rating
            rating={average_rating}
            reviewsCount={ratings_count ?? undefined}
          />
        ) : null
      }
      gap="1"
    >
      <Text fontSize="xs" color="gray.600" noOfLines={3}>
        <FormattedMessage
          {...messages[
            `categoryRecommendation.${image_slug}` as keyof typeof messages
          ]}
        />
      </Text>
    </RecommendationCard>
  );
};
